import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchWithSchema } from '@/lib/api';
import { getSchemaScopedStorageKey, getSchemaScopedQueryKey } from '@/lib/schemaStorage';
import type { AppSettings } from '@webready/shared/schema';

/**
 * Hook pentru sincronizarea limbii aplicației cu setările din baza de date.
 * Verifică default_language din app_settings și actualizează i18n dacă este necesar.
 */
export function useAppLanguage() {
  const { i18n } = useTranslation();

  // Fetch app settings pentru a obține default_language
  const { data: settings } = useQuery<AppSettings>({
    queryKey: getSchemaScopedQueryKey(['app-settings']),
    queryFn: async () => {
      try {
        const response = await fetchWithSchema('/api/app-settings');
        if (!response.ok) {
          throw new Error('Failed to fetch app settings');
        }
        return response.json();
      } catch (error) {
        console.warn('[useAppLanguage] Failed to fetch app settings, using fallback language:', error);
        return null;
      }
    },
    staleTime: 1000 * 60 * 5, // Cache pentru 5 minute
    retry: 1, // Retry o singură dată în caz de eroare
  });

  // Sincronizează limba când setările sunt încărcate
  useEffect(() => {
    if (settings?.default_language) {
      const dbLanguage = settings.default_language;
      
      // Verifică dacă limba din DB diferă de limba curentă i18n
      if (dbLanguage !== i18n.language && (dbLanguage === 'ro' || dbLanguage === 'en')) {
        // Actualizează limba i18n
        i18n.changeLanguage(dbLanguage);
        
        // Salvează în localStorage pentru consistență
        const storageKey = getSchemaScopedStorageKey('i18nextLng');
        localStorage.setItem(storageKey, dbLanguage);
      }
    }
  }, [settings?.default_language, i18n.language]);
}

